import { Send, Lock, Check } from "lucide-react";

/**
 * Sticky footer CTA for the primary client in interactive galleries (G4).
 *
 *   - Hidden for non-primary collaborators.
 *   - Hidden when no photo is favorited yet (nothing to submit).
 *   - Pre-submit  : shows live count + extra-overage hint + "Send selection" button.
 *   - Post-submit : shows "Selection submitted · N photos" badge (read-only).
 */
export default function SelectionFooterCta({
  role,
  isSubmitted,
  selectedCount,
  includedEdits,
  extraEditPrice,
  onOpenSubmit,
}) {
  if (role !== "primary") return null;
  if (!isSubmitted && selectedCount === 0) return null;

  if (isSubmitted) {
    return (
      <div className="fixed bottom-0 inset-x-0 z-[60] bg-stone-900 text-[#F5EFE0] border-t border-[#C9A961]/40" data-testid="selection-cta-submitted">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-center gap-3">
          <Check className="w-4 h-4 text-[#C9A961]" />
          <span className="text-[11px] uppercase tracking-[0.28em]">
            Selection submitted · <span className="text-white">{selectedCount}</span> photos
          </span>
          <Lock className="w-3 h-3 text-[#C9A961]/70 ml-1" />
        </div>
      </div>
    );
  }

  const extras = includedEdits > 0 ? Math.max(0, selectedCount - includedEdits) : 0;
  const overageTotal = (extras * (extraEditPrice || 0)).toFixed(2);

  return (
    <div className="fixed bottom-0 inset-x-0 z-[60] bg-stone-900 text-[#F5EFE0] border-t border-[#C9A961]/30" data-testid="selection-cta-bar">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs sm:text-sm">
          <span className="font-serif italic text-base sm:text-lg text-white" data-testid="selection-cta-count">
            {selectedCount}
            {includedEdits > 0 && <span className="text-[#F5EFE0]/60"> of {includedEdits}</span>}
          </span>{" "}
          <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#F5EFE0]/70">
            photo{selectedCount === 1 ? "" : "s"} selected
          </span>
          {extras > 0 && (
            <span className="ml-2 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#C9A961]" data-testid="selection-cta-overage">
              · {extras} extra (${overageTotal})
            </span>
          )}
        </div>
        <button
          onClick={onOpenSubmit}
          className="inline-flex items-center gap-2 px-4 sm:px-5 h-9 sm:h-10 bg-[#C9A961] text-stone-900 text-[10px] sm:text-[11px] uppercase tracking-[0.28em] font-medium hover:opacity-90 transition-opacity"
          data-testid="selection-cta-send-btn"
        >
          <Send className="w-3.5 h-3.5" />
          Send selection to photographer
        </button>
      </div>
    </div>
  );
}
